<template>
  <div>
    <InfoBar
      v-if="tokenError"
      class="mx-auto max-w-md"
      variant="destructive"
      :title="t('common.error')"
      :message="tokenError"
      @close="tokenError = null"
    />
    <UserDetailsForm
      v-else
      :initialValues="initialValues"
      emailDisabled
      :collectFields="collectFields"
      :requiredRules="requiredRules"
      :submitButtonText="isSubmitting ? t('common.submitting') : t('common.continue')"
      :error="errorMessage"
      :isSubmitting="isSubmitting"
      @submit="onSubmit"
      @clearError="clearError"
      @clearSuccess="clearSuccess"
    >
      <template #form-footer>
        <div
          class="mt-4 text-center text-xs text-foreground/50"
          v-html="t('dynamic.termsGeneral')"
        ></div>
      </template>
    </UserDetailsForm>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Base64 } from 'js-base64';
import { storeToRefs } from 'pinia';
import { useFanStore } from '@stores/fan.store';
import { useAccountStore } from '@stores/account.store';
import { type UserDetails } from '@modules/UserDetailsForm/UserDetails.interface';
import UserDetailsForm from '@modules/UserDetailsForm/UserDetailsForm.vue';
import InfoBar from '@generics/InfoBar.vue';
import { useI18n } from 'vue-i18n';
// import { useRouter } from 'vue-router';

const collectFields = {
  firstName: true,
  lastName: true,
  email: true,
  location: true,
  phoneNumber: true,
  birthDate: true,
  password: true,
  consentEmail: true,
  consentSms: true,
};

const requiredRules = {
  firstName: true,
  lastName: true,
  email: true,
  location: true,
  phoneNumber: true,
  birthDate: true,
  password: true,
  consentEmail: true,
  consentSms: true,
};

const { t } = useI18n();
// const router = useRouter();
const fanStore = useFanStore();
const accountStore = useAccountStore();
const { fanPatchError, fanData } = storeToRefs(fanStore);
const { signUpData, signUpError, loginFanError, tokenInQuery } = storeToRefs(accountStore);

const isSubmitting = ref(false);

// REVIEW
const tokenError = computed(() => {
  const token = tokenInQuery.value;
  if (!token) {
    return null;
  }

  try {
    if (!token.includes('.')) {
      return t('errors.invalidToken');
    }

    const [_, queryTokenBase64] = token.split('.');
    if (!queryTokenBase64) {
      return t('errors.invalidToken');
    }

    const queryTokenPayload = JSON.parse(Base64.decode(queryTokenBase64));
    if (!queryTokenPayload?.email || queryTokenPayload?.role !== 'signup') {
      return t('errors.invalidToken');
    }

    return null;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return t('errors.invalidToken');
  }
});

const fanEmail = computed(() => {
  if (!tokenInQuery.value) return fanData.value?.email; // how could it be

  try {
    const [, queryTokenBase64] = tokenInQuery.value.split('.');
    const queryTokenPayload = JSON.parse(Base64.decode(queryTokenBase64));
    return queryTokenPayload?.email;
  } catch {
    return null;
  }
});

const initialValues = computed(() => ({
  email: fanEmail.value || null,
}));

const errorMessage = computed(
  () =>
    signUpError.value?.message ||
    fanPatchError.value?.message ||
    loginFanError.value?.message ||
    '',
);

const clearError = () => {
  signUpError.value = null;
  fanPatchError.value = null;
  loginFanError.value = null;
};

const clearSuccess = () => {
  signUpData.value = null;
};

const onSubmit = async (formData: UserDetails) => {
  if (tokenError.value) return;

  isSubmitting.value = true;

  try {
    await accountStore.signUp(formData.password as string);
    if (signUpError.value) return;

    const { token: signupResponseToken } = signUpData.value;
    await fanStore.fanPatch(formData, signupResponseToken);
    if (fanPatchError.value) return;

    // NOTE: no need to remove query token, as it will be removed by loginFan
    await accountStore.loginFan(formData.email, formData.password as string);
  } catch (error) {
    console.error('Error', error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
